import { pipe, Record } from '@blade/std-lib'

/**
 * What the board shows, and how densely.
 *
 * These are the reader's own choices, not the account's: they live in this
 * browser and never reach the server, which is what the design's "saved to
 * this browser" line promises.
 */

export type Density = 'compact' | 'regular'

export type ColumnKey = 'new' | 'finalStretch' | 'migrated'

export type FieldKey =
  | 'socials'
  | 'risk'
  | 'netBought'
  | 'address'
  | 'bonding'
  | 'creator'

export type BoardDisplay = {
  density: Density
  columns: Readonly<Record<ColumnKey, boolean>>
  fields: Readonly<Record<FieldKey, boolean>>
}

export const COLUMN_KEYS: ReadonlyArray<ColumnKey> = [
  'new',
  'finalStretch',
  'migrated',
]

export const FIELD_KEYS: ReadonlyArray<FieldKey> = [
  'socials',
  'risk',
  'netBought',
  'address',
  'bonding',
  'creator',
]

export const DEFAULT_DISPLAY: BoardDisplay = {
  density: 'compact',
  columns: { new: true, finalStretch: true, migrated: true },
  fields: {
    socials: true,
    risk: true,
    netBought: true,
    address: true,
    bonding: true,
    creator: true,
  },
}

/**
 * Read what was stored, and forgive anything wrong with it.
 *
 * This is the reader's own browser, holding a blob that an older build wrote
 * and a newer one reads. A missing key takes its default and a junk value is
 * ignored, key by key — losing one preference is a small thing, but a board
 * that will not open because its settings are from last week is not.
 */
export const parseDisplay = (raw: string | undefined): BoardDisplay => {
  if (!raw) {
    return DEFAULT_DISPLAY
  }

  const stored: unknown = (() => {
    try {
      return JSON.parse(raw)
    } catch {
      return undefined
    }
  })()

  if (typeof stored !== 'object' || stored === null) {
    return DEFAULT_DISPLAY
  }

  const record = stored as Record<string, unknown>
  const flags = <Key extends string>(
    source: unknown,
    keys: ReadonlyArray<Key>,
    fallback: Readonly<Record<Key, boolean>>,
  ): Record<Key, boolean> => {
    const from = (
      typeof source === 'object' && source !== null ? source : {}
    ) as Record<string, unknown>

    return Object.fromEntries(
      keys.map((key) => [
        key,
        typeof from[key] === 'boolean' ? from[key] : fallback[key],
      ]),
    ) as Record<Key, boolean>
  }

  const columns = flags(record.columns, COLUMN_KEYS, DEFAULT_DISPLAY.columns)

  return {
    density: record.density === 'regular' ? 'regular' : 'compact',
    /* A board with no columns shows nothing and offers no way back, so a
       stored set that turns them all off is not honoured. */
    columns: COLUMN_KEYS.some((key) => columns[key])
      ? columns
      : DEFAULT_DISPLAY.columns,
    fields: flags(record.fields, FIELD_KEYS, DEFAULT_DISPLAY.fields),
  }
}

export const serialiseDisplay = (display: BoardDisplay): string =>
  JSON.stringify(display)

/**
 * Turn a column on or off, unless it is the last one left on.
 *
 * The menu is inside the board, so emptying the board takes the menu with it.
 */
export const toggleColumn = (
  display: BoardDisplay,
  key: ColumnKey,
): BoardDisplay => {
  const next = pipe(display.columns, Record.set(key, !display.columns[key]))
  if (!COLUMN_KEYS.some((column) => next[column])) {
    return display
  }
  return { ...display, columns: next }
}

export const toggleField = (
  display: BoardDisplay,
  key: FieldKey,
): BoardDisplay => ({
  ...display,
  fields: pipe(display.fields, Record.set(key, !display.fields[key])),
})

export const setDensity = (
  display: BoardDisplay,
  density: Density,
): BoardDisplay => ({ ...display, density })
